import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { GripVertical, Trash2, Image as ImageIcon, Type, Grid3X3, Minus, Quote, Images, ChevronDown, Plus, Pencil, Check, X, RotateCcw } from "lucide-react";
import { CaseStudySection, ContentBlockType, CONTENT_TYPES, CASE_STUDY_SECTIONS } from "@/types/case-study";
import { ContentBlockRenderer } from "./content-blocks/ContentBlockRenderer";

interface CaseStudySectionEditorProps {
  section: CaseStudySection;
  isActive: boolean;
  onUpdate: (updates: Partial<CaseStudySection>) => void;
  onRemove: () => void;
  onActivate: () => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  canDelete?: boolean;
  onUpdateContentBlock?: (blockId: string, content: Record<string, unknown>) => void;
  onRemoveContentBlock?: (blockId: string) => void;
  onAddContentBlock?: (type: ContentBlockType) => void;
  onAddSectionFromElement?: (elementType: ContentBlockType, insertAfterSectionId: string) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const CaseStudySectionEditor: React.FC<CaseStudySectionEditorProps> = ({
  section,
  isActive,
  onUpdate,
  onRemove,
  onActivate,
  dragHandleProps,
  canDelete = true,
  onUpdateContentBlock,
  onRemoveContentBlock,
  onAddContentBlock,
  onAddSectionFromElement,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const [showInsertOptions, setShowInsertOptions] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(section.title);
  const [titleError, setTitleError] = useState("");
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Check if this is a custom section
  const isCustomSection = section.type === "custom";

  // Get the default title for this section type
  const getDefaultTitle = () => {
    const sectionConfig = CASE_STUDY_SECTIONS.find(s => s.type === section.type);
    return sectionConfig?.title || section.title;
  };

  const defaultTitle = getDefaultTitle();
  const isUsingDefaultTitle = section.title === defaultTitle;

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const handleContentChange = (content: string) => {
    onUpdate({ content });
  };

  const handleStartEditTitle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditedTitle(section.title);
    setTitleError("");
    setIsEditingTitle(true);
  };

  const handleCancelEditTitle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditedTitle(section.title);
    setTitleError("");
    setIsEditingTitle(false);
  };

  const handleSaveTitle = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Validate title
    const trimmedTitle = editedTitle.trim();
    if (!trimmedTitle) {
      setTitleError("Title cannot be empty");
      return;
    }

    if (trimmedTitle.length > 100) {
      setTitleError("Title must be 100 characters or less");
      return;
    }

    // Save the title
    onUpdate({ title: trimmedTitle });
    setIsEditingTitle(false);
    setTitleError("");
  };

  const handleResetTitle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUpdate({ title: defaultTitle });
    setEditedTitle(defaultTitle);
    setIsEditingTitle(false);
    setTitleError("");
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveTitle(e as unknown as React.MouseEvent);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelEditTitle(e as unknown as React.MouseEvent);
    }
  };

  const activateSection = () => {
    if (!isEditingTitle) {
      onActivate();
    }
  };

  const handleHeaderClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEditingTitle) return;
    activateSection();
    if (onToggleCollapse) {
      onToggleCollapse();
    }
  };

  const handleElementClick = (elementType: ContentBlockType, e: React.MouseEvent) => {
    e.stopPropagation();
    // Add element directly inside the section
    if (onAddContentBlock) {
      onAddContentBlock(elementType);
    }
  };

  return (
    <Card
      className={`group transition-all ${
        isActive ? 'ring-2 ring-orange-500 shadow-lg' : 'hover:shadow-md'
      }`}
      data-section-id={section.id}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        activateSection();
      }}
    >
        <CardHeader
          className="cursor-pointer hover:bg-gray-50/50 transition-colors"
          onClick={handleHeaderClick}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div
                className="p-1 hover:bg-gray-100 rounded cursor-move flex-shrink-0"
                {...dragHandleProps}
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical className="h-4 w-4 text-gray-400" />
              </div>

              {/* Title - View Mode */}
              {!isEditingTitle && (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{section.title}</h3>
                  {!section.hideTitle && (
                    <button
                      onClick={handleStartEditTitle}
                      className="flex items-center justify-center w-6 h-6 rounded-full border border-gray-400  text-gray-600 transition-colors opacity-0 group-hover:opacity-100"
                      title="Edit section title"
                    >
                      <Pencil className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                  )}
                </div>
              )}

              {/* Title - Edit Mode */}
              {isEditingTitle && (
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  <div className="inline-block">
                    <input
                      ref={titleInputRef}
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      onKeyDown={handleTitleKeyDown}
                      className={`px-2 py-1 text-sm font-semibold border rounded focus:outline-none focus:ring-2 ${
                        titleError
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-gray-300 focus:ring-orange-500'
                      }`}
                      style={{ width: `${Math.max(editedTitle.length * 8 + 24, 120)}px` }}
                      placeholder="Enter section title"
                      maxLength={100}
                    />
                    {titleError && (
                      <p className="text-xs text-red-500 mt-1 whitespace-nowrap">{titleError}</p>
                    )}
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {/* Reset to default button - only show for non-custom sections with modified titles */}
                    {!isCustomSection && !isUsingDefaultTitle && (
                      <button
                        onClick={handleResetTitle}
                        className="p-1 hover:bg-blue-100 rounded transition-colors"
                        title={`Reset to default: "${defaultTitle}"`}
                      >
                        <RotateCcw className="h-3.5 w-3.5 text-blue-600" />
                      </button>
                    )}

                    {/* Save button */}
                    <button
                      onClick={handleSaveTitle}
                      className="p-1 hover:bg-green-100 rounded transition-colors"
                      title="Save title (Enter)"
                    >
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    </button>

                    {/* Cancel button */}
                    <button
                      onClick={handleCancelEditTitle}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                      title="Cancel (Esc)"
                    >
                      <X className="h-3.5 w-3.5 text-red-600" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              {!isCollapsed && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canDelete) {
                      onRemove();
                    }
                  }}
                  disabled={!canDelete}
                  className={`flex items-center justify-center w-6 h-6 rounded-full transition-colors ${
                    canDelete
                      ? 'bg-red-100 border-[1px] border-red-500 hover:bg-red-200 cursor-pointer'
                      : 'bg-gray-100 border-[1px] border-gray-300 cursor-not-allowed'
                  }`}
                  title={canDelete ? "Remove section" : "At least one section must remain"}
                >
                  <Trash2 className={`h-3 w-3 ${canDelete ? 'text-red-500' : 'text-gray-400'}`} />
                </button>
              )}
              {onToggleCollapse && (
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                    isCollapsed ? '' : 'rotate-180'
                  }`}
                />
              )}
            </div>
          </div>
        </CardHeader>
      
      {!isCollapsed && (
        <CardContent>
        <div className="space-y-4">
          {/* Only show RichTextEditor for sections that are not element-only */}
          {!section.hideTitle && (
            <RichTextEditor
              value={section.content}
              onChange={(value) => handleContentChange(value)}
              placeholder="Enter your content here..."
              maxLength={2000}
            />
          )}

          {/* Content Blocks */}
          {section.content_blocks && section.content_blocks.length > 0 && (
            <div className="space-y-4">
              {section.content_blocks
                .sort((a, b) => a.order - b.order)
                .map((block) => (
                  <ContentBlockRenderer
                    key={block.id}
                    block={block}
                    onUpdate={(content) => onUpdateContentBlock?.(block.id, content)}
                    onRemove={() => onRemoveContentBlock?.(block.id)}
                    isActive={isActive}
                    isRemovable={!section.hideTitle}
                  />
                ))}
            </div>
          )}

          {/* Insert Content Button - Only show for regular sections, not element-only sections */}
          {onAddContentBlock && !section.hideTitle && (
            <div className="">
              <div className="bg-white">
                <div className="flex items-center justify-center mb-4">
                   <button
                     onClick={(e) => {
                       e.stopPropagation();
                       setShowInsertOptions(!showInsertOptions);
                     }}
                     className="-mt-8 relative z-30 w-8 h-8 rounded-full bg-gray-50 border border-gray-200 shadow-sm hover:shadow-md hover:bg-gray-100 flex items-center justify-center transition-all duration-200 pointer-events-auto"
                     title={showInsertOptions ? "Hide options" : "Show options"}
                   >
                    {showInsertOptions ? (
                      <Minus className="h-3 w-3 text-gray-600" />
                    ) : (
                      <Plus className="h-3 w-3 text-gray-600" />
                    )}
                  </button>
                </div>
                {showInsertOptions && (
                  <div className="text-center">
                    <TooltipProvider delayDuration={200}>
                      <div className="flex items-center justify-center gap-4">
                        {CONTENT_TYPES.map((contentType) => {
                          const IconComponent = {
                            Type: Type,
                            Image: ImageIcon,
                            Grid3X3: Grid3X3,
                            Minus: Minus,
                            Quote: Quote,
                            Images: Images,
                          }[contentType.icon] || Type;

                          return (
                            <Tooltip key={contentType.type}>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={(e) => handleElementClick(contentType.type, e)}
                                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110 ${contentType.color}`}
                                >
                                  <IconComponent className="h-4 w-4" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="bg-gray-900 text-white border-gray-900">
                                <p className="text-xs font-medium">{contentType.title}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </TooltipProvider>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        </CardContent>
      )}
    </Card>
  );
};
